import {
  EventAdvancedConfigVisibilityRule,
  EventAdvancedConfigVisibilityRuleCreatePayload,
  EventAdvancedConfigVisibilityRuleUpdatePayload,
  EventAdvancedConfigVisibilityRulesQueryParams,
} from "../../models/rulesEventAdvancedConfig";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api } from "./api";

const ruleTag = (id: number) => ({
  type: "rulesEventAdvancedConfig" as const,
  id,
});

const ruleListTag = { type: "rulesEventAdvancedConfig" as const, id: "LIST" };

const toBoolean = (value: unknown): boolean => {
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }

  return value === true || value === 1 || value === "1" || value === "true";
};

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) {
    return response;
  }

  const record = response as Record<string, unknown>;
  if (record.success === true && record.data !== undefined) {
    return record.data;
  }

  return response;
};

const toNullableInt = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const toInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const mapRule = (raw: unknown): EventAdvancedConfigVisibilityRule => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  return {
    id: toInt(row.id),
    tenantId: String(row.tenantId ?? row.tenant_id ?? ""),
    eventTypeId: toNullableInt(row.eventTypeId ?? row.event_type_id),
    eventSubTypeId: toNullableInt(row.eventSubTypeId ?? row.event_sub_type_id),
    requiresRecurrent: toBoolean(row.requiresRecurrent ?? row.requires_recurrent),
    active: toBoolean(row.active),
    eventTypeName:
      row.eventTypeName == null && row.event_type_name == null
        ? undefined
        : String(row.eventTypeName ?? row.event_type_name ?? ""),
    eventSubTypeName:
      row.eventSubTypeName == null && row.event_sub_type_name == null
        ? null
        : String(row.eventSubTypeName ?? row.event_sub_type_name ?? ""),
    tenantName:
      row.tenantName == null && row.tenant_name == null
        ? null
        : String(row.tenantName ?? row.tenant_name ?? ""),
    createdAt:
      row.createdAt == null && row.created_at == null
        ? undefined
        : String(row.createdAt ?? row.created_at),
  };
};

const normalizeListResponse = (response: unknown): unknown[] => {
  const unwrapped = unwrapApiData(response);
  return Array.isArray(unwrapped) ? unwrapped : [];
};

const normalizeQueryArgs = (
  params: EventAdvancedConfigVisibilityRulesQueryParams = {}
): Record<string, string | number> => {
  const normalized: Record<string, string | number> = {};

  if (params.eventTypeId != null) {
    normalized.event_type_id = params.eventTypeId;
  }
  if (params.tenantId != null) {
    normalized.tenant_id = params.tenantId;
  }

  return normalized;
};

const listProvidesTags = (result: EventAdvancedConfigVisibilityRule[] | undefined) =>
  result
    ? [...result.map(({ id }) => ruleTag(id)), ruleListTag]
    : [ruleListTag];

const toRuleBody = (body: EventAdvancedConfigVisibilityRuleCreatePayload) => ({
  tenantId: body.tenantId,
  eventTypeId: body.eventTypeId,
  eventSubTypeId: body.eventSubTypeId,
  requiresRecurrent: toBoolean(body.requiresRecurrent),
  active: toBoolean(body.active),
});

type EbApiRootState = {
  ebAPI?: {
    queries?: Record<
      string,
      {
        endpointName?: string;
        originalArgs?: EventAdvancedConfigVisibilityRulesQueryParams;
      }
    >;
  };
};

const patchEventAdvancedConfigRulesListCaches = (
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  updater: (draft: EventAdvancedConfigVisibilityRule[]) => void
): Array<{ undo: () => void }> => {
  const patchResults: Array<{ undo: () => void }> = [];
  const rootState = getState() as EbApiRootState;

  for (const query of Object.values(rootState.ebAPI?.queries ?? {})) {
    if (query.endpointName !== "getEventAdvancedConfigVisibilityRules") {
      continue;
    }

    const cacheArgs = (query.originalArgs ??
      {}) as EventAdvancedConfigVisibilityRulesQueryParams;
    const cachedList =
      rulesEventAdvancedConfigApi.endpoints.getEventAdvancedConfigVisibilityRules.select(
        cacheArgs
      )(rootState as never);
    if (!cachedList.data) {
      continue;
    }

    patchResults.push(
      dispatch(
        rulesEventAdvancedConfigApi.util.updateQueryData(
          "getEventAdvancedConfigVisibilityRules",
          cacheArgs,
          updater
        )
      ) as { undo: () => void }
    );
  }

  return patchResults;
};

export const rulesEventAdvancedConfigApi = api.injectEndpoints({
  endpoints: (build) => ({
    getEventAdvancedConfigVisibilityRules: build.query<
      EventAdvancedConfigVisibilityRule[],
      EventAdvancedConfigVisibilityRulesQueryParams
    >({
      query: (params) => ({
        url: API_ENDPOINTS.RULES.EVENT_ADVANCED_CONFIG.BASE,
        params: normalizeQueryArgs(params),
      }),
      transformResponse: (response: unknown) =>
        normalizeListResponse(response).map(mapRule),
      providesTags: (result) => listProvidesTags(result),
    }),

    createEventAdvancedConfigVisibilityRule: build.mutation<
      EventAdvancedConfigVisibilityRule,
      EventAdvancedConfigVisibilityRuleCreatePayload
    >({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.EVENT_ADVANCED_CONFIG.BASE,
        method: "POST",
        body: toRuleBody(body),
      }),
      transformResponse: (response: unknown) => mapRule(unwrapApiData(response)),
      invalidatesTags: [ruleListTag],
    }),

    updateEventAdvancedConfigVisibilityRule: build.mutation<
      EventAdvancedConfigVisibilityRule,
      EventAdvancedConfigVisibilityRuleUpdatePayload
    >({
      query: (body) => ({
        url: API_ENDPOINTS.RULES.EVENT_ADVANCED_CONFIG.BASE,
        method: "PUT",
        body: {
          id: toInt(body.id),
          ...toRuleBody(body),
        },
      }),
      transformResponse: (response: unknown) => mapRule(unwrapApiData(response)),
      invalidatesTags: (_result, _error, { id }) => [ruleTag(id), ruleListTag],
    }),

    toggleEventAdvancedConfigVisibilityRuleActive: build.mutation<
      EventAdvancedConfigVisibilityRule,
      number
    >({
      query: (id) => ({
        url: API_ENDPOINTS.RULES.EVENT_ADVANCED_CONFIG.TOGGLE(id),
        method: "PATCH",
      }),
      transformResponse: (response: unknown) => mapRule(unwrapApiData(response)),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const optimisticPatches = patchEventAdvancedConfigRulesListCaches(
          dispatch,
          getState,
          (draft) => {
            const row = draft.find((item) => item.id === id);
            if (row) {
              row.active = !row.active;
            }
          }
        );

        try {
          const { data } = await queryFulfilled;
          patchEventAdvancedConfigRulesListCaches(dispatch, getState, (draft) => {
            const row = draft.find((item) => item.id === id);
            if (row) {
              Object.assign(row, data);
            }
          });
        } catch {
          optimisticPatches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [],
    }),
  }),
});

export const {
  useGetEventAdvancedConfigVisibilityRulesQuery,
  useCreateEventAdvancedConfigVisibilityRuleMutation,
  useUpdateEventAdvancedConfigVisibilityRuleMutation,
  useToggleEventAdvancedConfigVisibilityRuleActiveMutation,
} = rulesEventAdvancedConfigApi;
